import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DataHeader } from 'common/views/parts/parts-subcategory-details/parts-catalog-list/parts-catalog-list.component';
import { BehaviorSubject } from 'rxjs';
import { ToastService } from 'common/services/toast.service';
import { AccountAdminCompanyCreateService } from 'private/app/services/account-admin/account-admin-company-create.service';
import { AccountAdminCompanyUtilsService } from 'private/app/services/account-admin/account-admin-company-utils.service';
import { AccountAdminFormResultMap } from 'private/app/models/account-admin-search';
import { AddressLookupService } from 'private/app/services/address-lookup/address-lookup.service';
import { AddressLookupResult, ResultData } from 'private/app/models/address-lookup.model';

export interface AddressLookup {
    address1: string;
    address2: string;
    city: string;
    state: {
        label: string;
        code: string;
    };
    country: {
        label: string;
        code: string;
    };
    postalCode: string;
}

@Component({
    selector: 'hvac-address-lookup',
    templateUrl: './address-lookup.component.html',
    styleUrls: ['./address-lookup.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AddressLookupComponent implements OnInit {
    @Input() addressLookup: AddressLookup;
    @Input() visible: boolean;
    @Output() onAddressConfirmation = new EventEmitter<AddressLookup | null>();
    @Output() toastOpenStatus = new EventEmitter<boolean>(false);

    public toastOutlet = 'addressLookupToast';
    address$ = new BehaviorSubject<AddressLookup[]>([]);
    processStatus$ = new BehaviorSubject<boolean>(true);
    toastStatus$ = new BehaviorSubject<boolean>(true);
    public companyCreateForm = this.companyCreateService.companyCreateForm;
    public stateProvince: string = '';
    public isLoading: boolean = false;
    formData: AccountAdminFormResultMap;
    dataHeaders: DataHeader[] = [
        {
            title: this.translate.instant('ADDRESS_LOOKUP.ADDRESS_1'),
            value: 'address1',
            order: null
        },
        {
            title: this.translate.instant('ADDRESS_LOOKUP.ADDRESS_2'),
            value: 'address2',
            order: null
        },
        {
            title: this.translate.instant('ADDRESS_LOOKUP.CITY'),
            value: 'city',
            order: null
        },
        {
            title: this.translate.instant('ADDRESS_LOOKUP.STATE'),
            value: 'state',
            order: null
        },
        {
            title: this.translate.instant('ADDRESS_LOOKUP.COUNTRY'),
            value: 'country',
            order: null
        },
        {
            title: this.translate.instant('ADDRESS_LOOKUP.POSTAL_CODE'),
            value: 'postalCode',
            order: null
        },
        {
            title: this.translate.instant('ADDRESS_LOOKUP.ACTION'),
            value: 'action',
            order: null
        }
    ];

    private bannerType = 'AddressLookupToast';

    constructor(
        private readonly translate: TranslateService,
        private toast: ToastService,
        public accountAdminCompanyUtilsService: AccountAdminCompanyUtilsService,
        public readonly companyCreateService: AccountAdminCompanyCreateService,
        private addressLookupService: AddressLookupService
    ) {
    }

    ngOnInit(): void {
        this.addressLookupService.addressLookUpResult$.subscribe((resultData: AddressLookupResult) => {
            this.processStatus$.next(true);
            this.isLoading = false;

            const addressDetails = {
                address1: this.addressLookup?.address1,
                address2: this.addressLookup?.address2,
                city: this.addressLookup?.city,
                country: {
                    label: this.addressLookup?.country.code,
                    code: this.addressLookup?.country.code
                },
                state: {
                    label: this.addressLookup?.state.code,
                    code: this.addressLookup?.state.code
                },
                postalCode: this.addressLookup?.postalCode
            } as unknown as AddressLookup;

            this.address$.next([addressDetails]);

            if (this.addressLookupService.incorrectProcessStatus.includes(resultData?.Envelope?.Body.ProcessResponse.ProcessResult?.Results.Result.ProcessStatus.text || '') || !resultData || Object.keys(resultData).length === 0 || !resultData?.Envelope) {
                this.processStatus$.next(false);

                this.toast.add({
                    content: this.translate.instant('ADDRESS_LOOKUP.NO_MATCHING_ADDRESS_FOUND'),
                    theme: 'warning',
                    id: this.bannerType,
                    closeable: false,
                    autoClose: false,
                    outletName: this.toastOutlet
                });

                this.toastStatus$.next(true);
                this.toastOpenStatus.emit(true);

                return;
            }

            const result: ResultData[] | ResultData | null = resultData?.Envelope?.Body.ProcessResponse.ProcessResult?.Results.Result.ResultDataSet.ResultData || null;

            if (result) {
                if (Array.isArray(result)) {
                    this.address$.next([
                        ...result?.map((data) => ({
                            address1: data?.Address?.DeliveryAddressLines?.string?.text.replace(/#\s[A-Z]{2}/g, ''),
                            city: data?.Address?.Locality?.string?.text,
                            country: {
                                label: this.addressLookup?.country.label,
                                code: this.addressLookup?.country.code
                            },
                            state: {
                                label: this.getStateLabel(Array.isArray(data?.Address?.Province?.string) ? data.Address.Province.string[0]?.text : data?.Address?.Province?.string?.text),
                                code: Array.isArray(data?.Address?.Province?.string) ? data.Address.Province.string[0]?.text : data?.Address?.Province?.string?.text
                            },
                            postalCode: data?.Address?.PostalCode?.string?.text ?? ''
                        } as unknown as AddressLookup)),
                        ...[addressDetails]
                    ]);
                }
                else {
                    const dataObj = result as ResultData;
                    this.address$.next([
                        {
                            address1: (Array.isArray(dataObj?.Address?.DeliveryAddressLines?.string)) ? dataObj.Address.DeliveryAddressLines.string[0].text.replace(/#\s[A-Z]{2}/g, '') : dataObj.Address.DeliveryAddressLines.string.text.replace(/#\s[A-Z]{2}/g, ''),
                            city: dataObj?.Address?.Locality?.string?.text,
                            country: {
                                label: this.addressLookup?.country.label,
                                code: this.addressLookup?.country.code
                            },
                            state: {
                                label: this.getStateLabel(Array.isArray(dataObj?.Address?.Province?.string) ? dataObj.Address.Province.string[0]?.text : dataObj?.Address?.Province?.string?.text),
                                code: Array.isArray(dataObj?.Address?.Province?.string) ? dataObj.Address.Province.string[0]?.text : dataObj?.Address?.Province?.string?.text
                            },
                            postalCode: dataObj?.Address?.PostalCode?.string?.text
                        } as unknown as AddressLookup,
                        ...[addressDetails]
                    ]);
                }
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes?.addressLookup?.currentValue) {
            this.address$.next([]);
            this.isLoading = true;
            this.addressLookupService.addressDoctorLookUp(changes?.addressLookup?.currentValue);
        }
    }

    userAddress(address: AddressLookup) {
        this.onAddressConfirmation.emit(address);
        this.toast.remove(this.bannerType);
        this.toastOpenStatus.emit(false);
    }

    onSubmit() {
        this.onAddressConfirmation.emit(this.addressLookup);
    }

    onCancel() {
        this.onAddressConfirmation.emit(null);
        this.toast.remove(this.bannerType);
        this.toastOpenStatus.emit(false);
    }

    private getStateLabel(stateCode: string): string {
        const stateOptions = this.companyCreateService.stateOptionData$.value;
        const state = stateOptions?.find((option) => option.value === stateCode);

        return state?.name || stateCode;
    }
}
